/** `/ai-changes/pending` — MCP changes waiting for a person (backend metaplan A8, M24 rev 3).
 *
 *  On a production tenant (without MCP auto-apply) an MCP `apply` or `revert` changes nothing until
 *  someone applies it: an apply on its proposal's review page, a revert on its change set's page,
 *  a revert of a whole content batch on its own page (`/ai-changes/pending/:id`: it has no one
 *  change set). This list is where ops find them. */
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  aiEditKeys,
  listPending,
  pendingStatusColor,
  pendingStatusLabel,
  type AIPendingChange,
  type PendingFilters,
} from '@/api/aiEdit';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { formatDate } from '@/utils/format';

import { ReasonsLegend, ReasonValue } from './theirWords';

export function reviewHref(p: AIPendingChange): string | null {
  if (p.kind === 'apply' && p.proposal_id) return `/ai-changes/proposals/${p.proposal_id}`;
  if (p.kind === 'revert' && p.change_set_id) return `/ai-changes/${p.change_set_id}`;
  if (p.kind === 'revert' && p.batch_id) return pendingChangeHref(p.id);
  return null;
}

/** A pending change's own page (a batch revert's review page). */
export const pendingChangeHref = (id: string) => `/ai-changes/pending/${encodeURIComponent(id)}`;

/** "apply", "revert", "revert · batch". */
const kindLabel = (p: AIPendingChange) => (p.kind === 'revert' && p.batch_id ? 'revert · batch' : p.kind);

export function AIPendingChangesPage() {
  const [openOnly, setOpenOnly] = useState(true);
  const filters: PendingFilters = openOnly ? { status: 'pending,applying' } : {};
  const query = useQuery({
    queryKey: aiEditKeys.pending(filters),
    queryFn: () => listPending(filters),
  });
  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="Pending MCP changes"
        helpStory="ai-changes"
        subtitle="Production tenants’ MCP applies and reverts wait here until a person applies them."
        actions={
          <FormControlLabel
            control={<Switch checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
            label="Only open"
          />
        }
      />
      {query.isPending && <CircularProgress />}
      {query.isError && <Alert severity="error">Couldn’t load: {query.error.message}</Alert>}
      {query.data && query.data.results.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nothing is waiting.
        </Typography>
      )}
      {query.data && query.data.results.length > 0 && <ReasonsLegend />}
      {query.data && query.data.results.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Requested by</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>When</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.data.results.map((p) => {
              const href = reviewHref(p);
              return (
                <TableRow key={p.id} data-pending-row={p.id}>
                  <TableCell>
                    <Chip
                      size="small"
                      color={pendingStatusColor(p.status)}
                      label={pendingStatusLabel(p.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {href ? <RouterLink to={href}>{kindLabel(p)}</RouterLink> : kindLabel(p)}
                  </TableCell>
                  <TableCell>
                    <WithTenantMark environment={p.tenant_environment}>
                      {p.tenant_code}
                    </WithTenantMark>
                  </TableCell>
                  <TableCell>{p.requested_by?.email ?? '—'}</TableCell>
                  <TableCell>{p.oauth_client_name || p.oauth_client_id || '—'}</TableCell>
                  <TableCell>{p.created_ts ? formatDate(p.created_ts) : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <ReasonValue text={p.reason} clamp={3} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
